import moment from 'moment';

// Logic of the "Clés API" tab of the tenant record, kept out of any component.
//
// Dates: the API stores the chosen expiry date as the UTC end of day of that date (`2026-12-31`
// becomes 2026-12-31T23:59:59Z). Read in local time (Paris), it would fall on the next day: every
// display and every comparison of this date therefore goes through moment.utc.

export const KEY_TYPE = { FULL: 'FULL', SCOPED: 'SCOPED' };

export const KEY_TYPE_LABEL = {
  [KEY_TYPE.FULL]: 'Complète',
  [KEY_TYPE.SCOPED]: 'Limitée',
};

/** A key that expires within this many days is flagged "Expire bientôt". */
export const EXPIRING_SOON_DAYS = 30;

export const EXPIRY_PRESET = {
  DAYS_90: 'DAYS_90',
  YEAR_1: 'YEAR_1',
  NONE: 'NONE',
};

export const EMPTY_CREATE_FORM = {
  label: '',
  key_type: KEY_TYPE.FULL,
  scopes: [],
  expires_at: '',
  rate_limit_rps: '',
};

const DATE_INPUT_FORMAT = 'YYYY-MM-DD';
const LABEL_MAX_LENGTH = 100;

const STATUS_PRESENTATION = {
  ACTIVE: { label: 'Active', color: '#00b75f' },
  EXPIRING: { label: 'Expire bientôt', color: '#b7791f' },
  EXPIRED: { label: 'Expirée', color: '#dc2626' },
  REVOKED: { label: 'Révoquée', color: '#76808c' },
};

/** Display name of a key: its label, or its project for a legacy key without a label. */
export const getKeyDisplayName = (apiKey) =>
  apiKey.label?.trim() || apiKey.project;

/**
 * Displayed status. The API provides ACTIVE / EXPIRED / REVOKED; "Expire bientôt" is added on the
 * screen side for an active key that expires within EXPIRING_SOON_DAYS days.
 */
export const getKeyStatus = (apiKey, now = new Date()) => {
  let code = apiKey.status;

  if (
    code === 'ACTIVE' &&
    apiKey.expires_at &&
    moment.utc(apiKey.expires_at).diff(moment.utc(now), 'days', true) <=
      EXPIRING_SOON_DAYS
  ) {
    code = 'EXPIRING';
  }

  return { code, ...(STATUS_PRESENTATION[code] || STATUS_PRESENTATION.ACTIVE) };
};

/** Expiry date to display (day DD/MM/YYYY), or "—" when there is no expiry. */
export const formatExpiryDate = (expiresAt) =>
  expiresAt ? moment.utc(expiresAt).format('DD/MM/YYYY') : '—';

/** Value of an `<input type="date">` for an expiry shortcut; '' = no expiry. */
export const expiryFromPreset = (preset, now = new Date()) => {
  switch (preset) {
    case EXPIRY_PRESET.DAYS_90:
      return moment(now).add(90, 'days').format(DATE_INPUT_FORMAT);
    case EXPIRY_PRESET.YEAR_1:
      return moment(now).add(1, 'year').format(DATE_INPUT_FORMAT);
    default:
      return '';
  }
};

/** Earliest accepted expiry date: tomorrow, so that the API never rejects it. */
export const getMinExpiryDate = (now = new Date()) =>
  moment(now).add(1, 'day').format(DATE_INPUT_FORMAT);

const isDateAfterToday = (value, now) => {
  const date = moment(value, DATE_INPUT_FORMAT, true);

  return date.isValid() && date.isAfter(moment(now), 'day');
};

/**
 * The text of a number field read as a decimal number: surrounding blanks removed, and a decimal
 * comma (what a French keyboard types) read as a decimal point. The rate limit field is a text input
 * for that reason: a number input hands back an empty value for "2,5" in some browsers, which the
 * form would then take for "use the default limit" without telling anyone.
 */
const normalizeDecimal = (value) => String(value).trim().replace(',', '.');

const isRateLimitValid = (value, bounds) => {
  const text = normalizeDecimal(value);
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return false;

  const number = Number(text);
  return number >= bounds.min && number <= bounds.max;
};

/** Form checks, following the same rules as the API (which performs these checks again). */
export const validateCreateForm = (form, options, now = new Date()) => {
  const errors = {};
  const label = form.label.trim();

  if (!label) {
    errors.label = 'Le libellé est obligatoire.';
  } else if (label.length > LABEL_MAX_LENGTH) {
    errors.label = `Le libellé ne peut dépasser ${LABEL_MAX_LENGTH} caractères.`;
  }

  if (form.key_type === KEY_TYPE.SCOPED && form.scopes.length === 0) {
    errors.scopes = 'Choisissez au moins un scope.';
  }

  if (form.expires_at && !isDateAfterToday(form.expires_at, now)) {
    errors.expires_at = 'La date de fin doit être postérieure à aujourd’hui.';
  }

  const bounds = options?.rate_limit_rps;
  if (
    form.rate_limit_rps !== '' &&
    bounds &&
    !isRateLimitValid(form.rate_limit_rps, bounds)
  ) {
    errors.rate_limit_rps = `Entre ${bounds.min} et ${bounds.max} appels par seconde, deux décimales au plus.`;
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

/** POST body: contains only what was filled in. */
export const buildCreatePayload = (form) => ({
  label: form.label.trim(),
  key_type: form.key_type,
  ...(form.key_type === KEY_TYPE.SCOPED && { scopes: form.scopes }),
  ...(form.expires_at && { expires_at: form.expires_at }),
  ...(form.rate_limit_rps !== '' && {
    rate_limit_rps: Number(normalizeDecimal(form.rate_limit_rps)),
  }),
});

/** A public API key, the only kind that carries a rate limit and scopes. */
const isPublicKey = (apiKey) => !apiKey.is_legacy;

const toDateInput = (expiresAt) =>
  expiresAt ? moment.utc(expiresAt).format(DATE_INPUT_FORMAT) : '';

/** Initial values of the form used to edit a key. */
export const toEditForm = (apiKey) => ({
  label: apiKey.label || '',
  scopes: [...(apiKey.scopes || [])],
  expires_at: toDateInput(apiKey.expires_at),
  rate_limit_rps:
    apiKey.rate_limit_rps === null || apiKey.rate_limit_rps === undefined
      ? ''
      : String(apiKey.rate_limit_rps),
});

const compareText = (a, b) => a.localeCompare(b);

const sameScopes = (a, b) =>
  [...a].sort(compareText).join() === [...b].sort(compareText).join();

/**
 * PUT body: only the modified fields. `null` removes the expiry or restores the default rate
 * limit. A legacy key never receives a rate limit or scopes, and a full key never receives scopes:
 * the API would reject them.
 */
export const buildUpdatePayload = (apiKey, form) => {
  const payload = {};
  const current = toEditForm(apiKey);

  if (form.label.trim() !== current.label.trim()) {
    payload.label = form.label.trim();
  }

  if (form.expires_at !== current.expires_at) {
    payload.expires_at = form.expires_at || null;
  }

  // Compared as numbers: "2,5" and "2.5" are the same limit and must not count as a change.
  const formRateLimit =
    form.rate_limit_rps === '' ? '' : normalizeDecimal(form.rate_limit_rps);

  if (isPublicKey(apiKey) && formRateLimit !== current.rate_limit_rps) {
    payload.rate_limit_rps =
      formRateLimit === '' ? null : Number(formRateLimit);
  }

  if (
    isPublicKey(apiKey) &&
    apiKey.key_type === KEY_TYPE.SCOPED &&
    !sameScopes(form.scopes, current.scopes)
  ) {
    payload.scopes = form.scopes;
  }

  return payload;
};

/**
 * Edit form checks. More lenient than on creation: a legacy key has no label and may stay that
 * way, and an expiry date that is already past but unchanged is not rejected (otherwise the label
 * of an expired key could no longer be edited).
 */
export const validateEditForm = (apiKey, form, options, now = new Date()) => {
  const errors = {};
  const current = toEditForm(apiKey);
  const label = form.label.trim();

  if (!label && current.label.trim()) {
    errors.label = 'Le libellé ne peut pas être vidé.';
  } else if (label.length > LABEL_MAX_LENGTH) {
    errors.label = `Le libellé ne peut dépasser ${LABEL_MAX_LENGTH} caractères.`;
  }

  if (
    form.expires_at &&
    form.expires_at !== current.expires_at &&
    !isDateAfterToday(form.expires_at, now)
  ) {
    errors.expires_at = 'La date de fin doit être postérieure à aujourd’hui.';
  }

  if (isPublicKey(apiKey)) {
    const bounds = options?.rate_limit_rps;
    if (
      form.rate_limit_rps !== '' &&
      bounds &&
      !isRateLimitValid(form.rate_limit_rps, bounds)
    ) {
      errors.rate_limit_rps = `Entre ${bounds.min} et ${bounds.max} appels par seconde, deux décimales au plus.`;
    }

    if (apiKey.key_type === KEY_TYPE.SCOPED && form.scopes.length === 0) {
      errors.scopes = 'Choisissez au moins un scope.';
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

/** Rate limit to display in the list. */
export const describeRateLimit = (apiKey) => {
  if (apiKey.is_legacy || apiKey.effective_rate_limit_rps == null) {
    return 'Non limitée';
  }

  const rate = String(apiKey.effective_rate_limit_rps).replace('.', ',');
  const suffix = apiKey.rate_limit_rps == null ? ' (défaut)' : '';

  return `${rate} appels/s${suffix}`;
};
